import { DynamicMechanicalSample } from "@/pages/laboratory/models/DynamycMechanicalSample";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import {
	CellClassParams,
	ColDef,
	ColGroupDef,
	ValueFormatterParams,
} from "ag-grid-enterprise";
import { useCallback, useMemo } from "react";
import { CatalogMechanicalTest } from "../../../models/CatalogMechanicalTest";
import { CatalogResultSource } from "../../../models/CatalogResultSource";

interface UseTableDataProps {
	resultSources: CatalogResultSource[];
	mechanicalTests: CatalogMechanicalTest[];
}

export const useTableData = ({
	resultSources,
	mechanicalTests,
}: UseTableDataProps) => {
	// Utility functions wrapped in useCallback for stable references
	const numberRichFormatter = useCallback(
		(params: ValueFormatterParams, decimals: number) => {
			if (params.value === null || isNaN(params.value)) return "";
			return parseFloat(params.value).toFixed(decimals);
		},
		[],
	);

	const lookupResultSourceValue = useCallback(
		(resultSources: CatalogResultSource[], resultSourceId: string) => {
			const id = isNaN(parseInt(resultSourceId)) ? 0 : parseInt(resultSourceId);
			const sampleObject = resultSources.find(
				(resultSource) => resultSource.ResultSourceId === id,
			);
			return sampleObject?.ResultSourceName || "";
		},
		[],
	);

	const cellNumberClass = useCallback((params: CellClassParams) => {
		if (params.value === null || isNaN(params.value)) return "error-limit";
		const sample: DynamicMechanicalSample = params.data;
		if (!sample?.Limits) return "error-limit";

		const limit = sample.Limits.find(
			(limit) =>
				limit.MechanicalTestId === parseInt(params.colDef.field ?? "0"),
		);
		if (!limit) return "error-limit2";

		const { MinValue: minLimit, MaxValue: maxLimit } = limit;
		return (minLimit !== null && parseFloat(params.value) < minLimit) ||
			(maxLimit !== null && parseFloat(params.value) > maxLimit)
			? "error-limit"
			: "error-limit2";
	}, []);

	// Create stable formatter functions
	const widthFormatter = useCallback(
		(params: ValueFormatterParams) => numberRichFormatter(params, 4),
		[numberRichFormatter],
	);

	const thicknessFormatter = useCallback(
		(params: ValueFormatterParams) => numberRichFormatter(params, 4),
		[numberRichFormatter],
	);

	const resultSourceFormatter = useCallback(
		(params: ValueFormatterParams) =>
			lookupResultSourceValue(resultSources, params.value),
		[lookupResultSourceValue, resultSources],
	);

	// Sample information columns
	const sampleColumns: (ColDef | ColGroupDef)[] = useMemo(
		() => [
			{
				headerName: "Sample",
				type: ["centerAligned"],
				children: [
					{
						field: "HeatName",
						headerName: "Heat",
						minWidth: 78,
						flex: 1,
					},
					{
						field: "SampleSequence",
						headerName: "Seq",
						minWidth: 50,
						flex: 1,
					},
					{
						field: "GradeName",
						headerName: "Grade",
						minWidth: 86,
						flex: 1,
					},
					{
						field: "SectionName",
						headerName: "Section",
						minWidth: 96,
						flex: 1,
					},
					{
						field: "Width",
						headerName: "Width",
						minWidth: 85,
						type: ["numericColumn"],
						valueFormatter: widthFormatter,
						headerClass: "stripe-header",
						flex: 1,
					},
					{
						field: "Thickness",
						headerName: "Thickness",
						minWidth: 113,
						type: ["numericColumn"],
						valueFormatter: thicknessFormatter,
						headerClass: "stripe-header",
						flex: 1,
					},
					{
						field: "ResultSourceId",
						headerName: "Source",
						minWidth: 93,
						flex: 1,
						valueFormatter: resultSourceFormatter,
					},
					{
						field: "Method",
						headerName: "Method",
						minWidth: 97,
						flex: 1,
					},
				],
			},
		],
		[widthFormatter, thicknessFormatter, resultSourceFormatter],
	);

	// Results columns
	const resultColumns: (ColDef | ColGroupDef)[] = useMemo(
		() => [
			{
				headerName: "Results",
				children: [
					{
						field: "DateTimeResults",
						headerName: "Result",
						valueGetter: (params) =>
							dxtToLocalServerTime(
								`${params.data.DateTimeResults}`,
								"MM/dd/yyyy - HH:mm",
							) || "",
						minWidth: 150,
						flex: 1,
					},
					{
						field: "Technician",
						headerName: "Technician",
						minWidth: 117,
						flex: 1,
					},
				],
			},
		],
		[],
	);

	// Mechanical test columns
	const mechanicalTestColumns = useMemo(() => {
		const columns: ColDef[] = mechanicalTests
			.filter((mechanicalTest) => mechanicalTest.IsActive) // FILTER %RA
			.map((mechanicalTest) => {
				// Create a stable formatter for each test's decimal places
				const formatter = (params: ValueFormatterParams) =>
					numberRichFormatter(params, mechanicalTest.Decimals);

				return {
					field: mechanicalTest.MechanicatTestId?.toString(),
					headerName: mechanicalTest.MechanicalTestName,
					minWidth: 116,
					flex: 1,
					headerClass: mechanicalTest.UseInCircular ? "stripe-header" : "",
					valueFormatter: formatter,
					cellClass: cellNumberClass,
				};
			});

		return [
			{
				headerName: "Mechanical Tests",
				children: columns,
			},
		];
	}, [mechanicalTests, numberRichFormatter, cellNumberClass]);

	// Combine all columns
	const allColumns = useMemo(
		() => [...sampleColumns, ...mechanicalTestColumns, ...resultColumns],
		[sampleColumns, mechanicalTestColumns, resultColumns],
	);

	// Default column definitions
	const defaultColDef = useMemo(
		() => ({
			flex: 1,
			resizable: true,
			sortable: true,
			filter: true,
			editable: false,
			tooltipComponent: "customTooltip",
			tooltipComponentParams: { color: "#ececec" },
		}),
		[],
	);

	return {
		columns: allColumns,
		defaultColDef,
	};
};
